import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from "react";

import { SelectData } from "../../../../models";
import { getWorkflowTemplate, GetWorkflowTemplateResponse, searchWorkflowTemplates } from "../../../../service/query";
import { Status } from "../../../../service/Shared";
import { getWorkflowTemplateExpiryFilterGroups } from "../../workflows";

interface UseSelectWorkflowTemplateModalProps {
  show: boolean;
  closeModal: () => void;
  projectTypeUuid?: string;
  workflowType: "Deal" | "Project";
}

interface UseSelectWorkflowTemplateModalReturnData {
  selectedTemplate: GetWorkflowTemplateResponse | undefined;
  selectedTemplateUuid: string | undefined;
  templateList: SelectData;
  showApplyWorkflowTemplatePanel: boolean;
  handleSelectTemplate: (value: string) => void;
  handleTemplateSearch: (search: string) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  setShowApplyWorkflowTemplatePanel: Dispatch<SetStateAction<boolean>>;
  onCancel: () => void;
}

export const useSelectWorkflowTemplateModal = ({
  show,
  closeModal,
  projectTypeUuid,
  workflowType,
}: UseSelectWorkflowTemplateModalProps): UseSelectWorkflowTemplateModalReturnData => {
  const [showApplyWorkflowTemplatePanel, setShowApplyWorkflowTemplatePanel] = useState(false);

  const [templateList, setTemplateList] = useState<SelectData>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<GetWorkflowTemplateResponse>();
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string>();

  const onCancel = (): void => {
    setShowApplyWorkflowTemplatePanel(false);
    setTemplateList([]);
    setSelectedTemplate(undefined);
    setSelectedTemplateUuid(undefined);
    closeModal();
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setShowApplyWorkflowTemplatePanel(true);
  };

  const handleSelectTemplate = async (value: string): Promise<void> => {
    if (value === "") return;

    const response = await getWorkflowTemplate({ workflowTemplateUuid: value });
    if (response.status === Status.Success && response.data) {
      setSelectedTemplateUuid(value);
      setSelectedTemplate(response.data);
    }
  };

  const handleTemplateSearch = async (search: string): Promise<void> => {
    if (workflowType === "Project" && !projectTypeUuid) {
      setTemplateList([]);
      return;
    }

    const response = await searchWorkflowTemplates({
      ...getWorkflowTemplateExpiryFilterGroups(false),
      paging: {
        limit: 10,
        beforeCursor: "",
        afterCursor: "",
      },
      sort: [
        {
          key: "results.name",
          direction: "asc",
        },
      ],
      filter: {
        results: {
          // eslint-disable-next-line no-nested-ternary
          ...(workflowType === "Project" && projectTypeUuid
            ? {
                projectType: {
                  uuid: {
                    operator: "eq",
                    value: projectTypeUuid,
                  },
                },
              }
            : workflowType === "Deal"
              ? {
                  type: {
                    operator: "eq",
                    value: workflowType,
                  },
                }
              : {}),
          name: {
            operator: "startsWith",
            value: search,
          },
        },
      },
    });

    if (response.status === Status.Success && response.data?.results) {
      const list = response.data.results.flatMap((el) => {
        if (el.uuid && el.name && (el.validTo == null || el.validTo >= new Date())) {
          return { key: el.uuid, value: el.name };
        }
        return [];
      });

      setTemplateList(list);
    }
  };

  useEffect(() => {
    if (show) handleTemplateSearch("");
  }, [show]);

  return {
    templateList,
    selectedTemplate,
    selectedTemplateUuid,
    showApplyWorkflowTemplatePanel,
    handleSelectTemplate,
    handleTemplateSearch,
    handleSubmit,
    setShowApplyWorkflowTemplatePanel,
    onCancel,
  };
};
